// Turns period-analytics rows into the shape the table and Excel export
// render. No aggregation happens here beyond summing across the selected
// locations - the backend already bucketed every period, converted units and
// applied each KPI's SUM/LATEST aggregation.

/**
 * Reports whether a value can be treated as a number (excluding null,
 * undefined and empty string, which Number() would coerce to 0).
 *
 * @param {*} val - Value to test.
 * @returns {boolean} True when val is numeric.
 */
const isValidNumber = (val) =>
  val !== null && val !== undefined && val !== "" && !isNaN(Number(val));

/**
 * Formats a cell value for display, keeping up to two decimal places without
 * rounding: extra digits past the second are truncated (12.999 -> "12.99"),
 * trailing zeros are dropped (12.90 -> "12.9"), and a wholly-zero fraction is
 * omitted (12.00 -> "12"). The integer part uses en-IN grouping.
 *
 * @param {*} value - Raw value; null/undefined/"" render as "No Answer".
 * @param {string} [unit] - Optional unit appended to the number.
 * @returns {string} Display string, e.g. "300", "12.5 GJ" or "No Answer".
 */
export const formatDisplayValue = (value, unit) => {
  if (value === null || value === undefined || value === "") {
    return "No Answer";
  }

  if (!isValidNumber(value)) {
    return String(value);
  }

  const num = Number(value);
  const negative = num < 0;
  const [intDigits, decDigits = ""] = Math.abs(num).toString().split(".");
  const decimals = decDigits.slice(0, 2).replace(/0+$/, "");
  const grouped = Number(intDigits).toLocaleString("en-IN");
  const magnitude = decimals ? `${grouped}.${decimals}` : grouped;
  const formatted = negative && magnitude !== "0" ? `-${magnitude}` : magnitude;

  return unit ? `${formatted} ${unit}` : formatted;
};

/**
 * Strips HTML tags from a question title and converts <br> to newlines, so the
 * title renders as plain text (never via dangerouslySetInnerHTML) both on the
 * screen and in the Excel export. This prevents stored markup in a title from
 * being interpreted as HTML - the source of a potential stored-XSS - while
 * preserving intended line breaks as "\n".
 *
 * @param {string} title - Raw question title (may contain markup).
 * @returns {string} Plain-text title with line breaks as "\n".
 */
export const plainTitle = (title) =>
  String(title || "-")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .trim() || "-";

/**
 * Lists the parent locations within the current selection, as {value, label}
 * pairs. Used for the per-location Excel sheets: the value scopes each sheet's
 * rows, the label names the sheet. An empty or all-inclusive selection means
 * every location. Analytics rows carry numeric location ids, so sheets are
 * scoped by id rather than by display name.
 *
 * @param {string[]} [selectedLocationIds=[]] - Selected MultiSelect values.
 * @param {Array<{value: string, label: string}>} [locations=[]] - All options.
 * @returns {Array<{value: string, label: string}>} Selected parent locations.
 */
export const getSelectedParentLocations = (selectedLocationIds = [], locations = []) => {
  if (selectedLocationIds.length === 0 || selectedLocationIds.length === locations.length) {
    return locations;
  }
  const selected = new Set(selectedLocationIds.map(String));
  return locations.filter((loc) => selected.has(String(loc.value)));
};

/**
 * Builds the render map from period-analytics rows, summing each question's
 * values across the locations and sub-locations the filters allow. Rows arrive
 * keyed by question id with a sparse `values` map covering every aggregation's
 * columns at once, so no tag matching or re-bucketing happens here and the
 * period filter is satisfied purely by picking different keys.
 *
 * Because `values` is sparse, a column is present only where a record actually
 * covered it: an absent key means "not answered", while a present 0 means a
 * reported zero. That distinction is preserved through the sum.
 *
 * @param {Object} params
 * @param {Array<Object>} [params.rows=[]] - Analytics rows, each carrying qid,
 *   locationId, subLocationId, unit and values (bucket key -> number).
 * @param {Array<Object>} [params.questions=[]] - Questions being rendered.
 * @param {string[]} [params.selectedLocationIds=[]] - Selected location values.
 * @param {Array<{value: string, label: string}>} [params.locations=[]] - All options.
 * @param {string[]} [params.selectedSubLocationIds=[]] - Selected sub-location
 *   values. Filtering is per parent: a parent with selected subs is narrowed to
 *   them; a parent with none stays fully aggregated.
 * @param {Array<{value: string, label: string, parentValue: string}>} [params.subLocations=[]]
 *   - All sub-location options; parentValue ties a sub to its parent location id.
 * @param {string[]} [params.restrictLocationIds=null] - Limit to these location
 *   ids, overriding selectedLocationIds (used for per-location Excel sheets).
 * @returns {Object<string, Object<string, {value: number, unit: string}>>}
 *   questionId -> period bucket key -> cell.
 */
export const buildQuickViewMap = ({
  rows = [],
  questions = [],
  selectedLocationIds = [],
  locations = [],
  selectedSubLocationIds = [],
  subLocations = [],
  restrictLocationIds = null,
}) => {
  const renderedQuestionIds = new Set(questions.map((q) => String(q.questionId)));

  // Analytics rows carry numeric location ids, so filtering compares ids
  // directly. A full (or empty) selection means "every location".
  let allowedLocationIds = null;
  if (restrictLocationIds) {
    allowedLocationIds = new Set(restrictLocationIds.map(String));
  } else if (selectedLocationIds.length > 0 && selectedLocationIds.length < locations.length) {
    allowedLocationIds = new Set(selectedLocationIds.map(String));
  }

  // Sub-location filtering is per parent location: a location that has selected
  // sub-locations is narrowed to just those, while a location with none stays
  // fully aggregated. Parents absent from the map are unrestricted.
  let subIdsByParent = null;
  if (selectedSubLocationIds.length > 0) {
    subIdsByParent = new Map();
    selectedSubLocationIds.forEach((id) => {
      const sub = subLocations.find((s) => String(s.value) === String(id));
      if (!sub) return;
      const parentKey = String(sub.parentValue);
      if (!subIdsByParent.has(parentKey)) subIdsByParent.set(parentKey, new Set());
      subIdsByParent.get(parentKey).add(String(sub.value));
    });
  }

  const quickViewMap = {};

  rows.forEach((row) => {
    const questionId = String(row.qid);
    if (!renderedQuestionIds.has(questionId)) return;

    const locationId = String(row.locationId);
    if (allowedLocationIds && !allowedLocationIds.has(locationId)) return;

    if (subIdsByParent) {
      const allowedSubIds = subIdsByParent.get(locationId);
      if (allowedSubIds && !allowedSubIds.has(String(row.subLocationId))) return;
    }

    const values = row.values || {};
    Object.keys(values).forEach((bucketKey) => {
      const value = Number(values[bucketKey]);
      if (!Number.isFinite(value)) return;

      if (!quickViewMap[questionId]) quickViewMap[questionId] = {};
      const existing = quickViewMap[questionId][bucketKey];
      quickViewMap[questionId][bucketKey] = {
        value: (existing ? existing.value : 0) + value,
        unit: row.unit ?? existing?.unit ?? null,
      };
    });
  });

  return quickViewMap;
};

/**
 * Groups questions by their reporting-UI module name for table sectioning.
 *
 * @param {Array<Object>} [questions=[]] - Question objects.
 * @returns {Object<string, Array<Object>>} Module name -> questions.
 */
export const groupQuestionsByModule = (questions = []) => {
  return questions.reduce((acc, question) => {
    const key = question.moduleName || "Uncategorized";
    if (!acc[key]) acc[key] = [];
    acc[key].push(question);
    return acc;
  }, {});
};

export { isValidNumber };
